import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  type AgentSharedConversationCreate, type AgentSharedTurnRequest,
  useListAgentSharedConversations, listAgentSharedConversations, createAgentSharedConversation,
  getListAgentSharedConversationsQueryKey
} from '@cdf/api-client-react';
import { Paperclip } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AgentComposerInput, AgentWelcome, type AgentConversationPresentation } from './agent-chat-presentation';
import { SharedAgentFooter } from './shared-agent-footer';
import { AgentPromptLibrary } from './agent-prompt-library';
import { useAgentRailLayout } from './use-agent-rail-layout';
import { useDraftNavigationGuard } from '@/hooks/use-draft-navigation-guard';
import { sharedContextKey, sharedContextParams, sameSharedContext, type SharedAgentRecord } from './shared-chat-context';
import { workspaceError } from './workspace-utils';

export type InitialSharedQuestion = Pick<AgentSharedTurnRequest, 'message' | 'mode'>;
interface Props {
  onNewChat?: () => void;
  presentation?: AgentConversationPresentation;
  compact?: boolean;
  identity: string;
  record: SharedAgentRecord;
  disabled: boolean;
  onReady: (id: string, question?: InitialSharedQuestion, sendOnReady?: boolean) => void;
  onBusy: (busy: boolean) => void;
  onLeaveGuardChange: (guard: (() => boolean) | null) => void;
}
function remembered(key: string) {
  try { const id = sessionStorage.getItem(key); return id && /^[0-9a-f-]{36}$/i.test(id) ? id : undefined; }
  catch { return undefined; }
}
/** Resolves a default with reads; only the user's Send may create it. */
export function SharedAgentStart({ identity, record, disabled, onReady, onBusy, onLeaveGuardChange, presentation, compact = false, onNewChat }: Props) {
  const client = useQueryClient();
  const rails = useAgentRailLayout(compact);
  const [libraryBusy, setLibraryBusy] = React.useState(false), [libraryDirty, setLibraryDirty] = React.useState(false);
  const storageKey = 'agent-shared-default:' + sharedContextKey(identity, record.context);
  const [reservation] = React.useState<AgentSharedConversationCreate>(() => ({
    context: record.context, title: 'Shared conversation', useDefault: true,
    clientConversationId: remembered(storageKey) ?? crypto.randomUUID()
  }));
  const [uncertain, setUncertain] = React.useState(() => Boolean(remembered(storageKey)));
  const [message, setMessage] = React.useState('');
  const [speechActive, setSpeechActive] = React.useState(false);
  const [busy, setBusy] = React.useState(false), [error, setError] = React.useState<string>();
  const controller = React.useRef(new AbortController()), alive = React.useRef(true), flight = React.useRef(false);
  const guard = React.useRef<() => boolean>(() => true);
  const params = { ...sharedContextParams(record.context), limit: 1, offset: 0 };
  const queryKey = [...getListAgentSharedConversationsQueryKey(params), identity];
  const query = useListAgentSharedConversations(params, { query: { queryKey, retry: false, staleTime: 0, gcTime: 0 } });
  const current = !query.isFetching && !query.isError && Boolean(query.data);
  guard.current = () => !speechActive && !busy && !libraryBusy && !uncertain && (!(message.trim() || libraryDirty) || window.confirm('Leave this shared chat and discard unsent work?'));
  useDraftNavigationGuard(Boolean(message.trim() || libraryDirty), busy || libraryBusy || uncertain || speechActive);
  React.useEffect(() => {
    alive.current = true;
    if (controller.current.signal.aborted) controller.current = new AbortController();
    onLeaveGuardChange(() => guard.current());
    return () => { alive.current = false; controller.current.abort(); onBusy(false); onLeaveGuardChange(null); void client.cancelQueries({ queryKey, exact: true }); };
  }, [client, onBusy, onLeaveGuardChange, JSON.stringify(queryKey)]);
  React.useEffect(() => {
    if (current && query.data?.defaultConversationId && !busy && !libraryBusy && !libraryDirty && !uncertain && !message.trim())
      onReady(query.data.defaultConversationId);
  }, [current, query.data?.defaultConversationId, busy, libraryBusy, libraryDirty, uncertain, message, onReady]);
  function forgetReservation() {
    try { sessionStorage.removeItem(storageKey); } catch { /* In-memory recovery still works. */ }
  }
  async function start(recover = false) {
    if (flight.current || libraryBusy || libraryDirty || disabled || !record.canWrite || (!recover && (!current || uncertain || !message.trim()))) return;
    flight.current = true; setBusy(true); onBusy(true); setError(undefined);
    try {
      let id: string;
      if (recover) {
        const found = await listAgentSharedConversations(params, { signal: controller.current.signal });
        if (!found.defaultConversationId) throw new Error('The original chat is not available yet. Check again before sending anything.');
        id = found.defaultConversationId;
      } else {
        try { sessionStorage.setItem(storageKey, reservation.clientConversationId); } catch { /* Retain the identity in memory. */ }
        const row = await createAgentSharedConversation(reservation, { signal: controller.current.signal });
        if (!sameSharedContext(row.context, record.context) || row.deleted) throw new Error('The shared chat is unavailable for this record.');
        id = row.id;
      }
      if (!alive.current) return;
      forgetReservation(); setUncertain(false);
      onReady(id, message.trim() ? { message: message.trim(), mode: 'shared' } : undefined, !recover);
      void client.invalidateQueries({ queryKey: getListAgentSharedConversationsQueryKey() });
    } catch (failure) {
      if (alive.current) { setError(workspaceError(failure)); setUncertain(true); }
    } finally {
      flight.current = false;
      if (alive.current) { setBusy(false); onBusy(false); }
    }
  }
  const writable = current && record.canWrite && !disabled && !busy && !libraryBusy && !uncertain;
  const composer = React.useRef<HTMLTextAreaElement>(null);
  function prepareQuestion(content: string) {
    if (!writable || content.length > 4000) return false;
    if (message.trim() && message !== content && !window.confirm('Replace your unsent message with this prompt?')) return false;
    setMessage(content); composer.current?.focus(); return true;
  }
  return <div ref={rails.ref} className="flex min-h-0 min-w-0 flex-1">
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
    <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3 text-sm text-muted-foreground">
      <AgentWelcome heading={presentation?.heading} introduction={presentation?.introduction ?? 'Ask about this record or choose a prompt.'}
        guidance={presentation?.guidance ?? 'Your question and answer are shared with current record readers.'}
        questions={presentation?.starterQuestions ?? record.starterQuestions ?? []} compact={compact} disabled={!writable} onQuestion={prepareQuestion} />
      {query.isFetching ? <p role="status">Checking the shared conversation…</p> :
        query.isError ? <div><p role="alert">{workspaceError(query.error)}</p><Button variant="outline" onClick={() => { void query.refetch(); }}>Check current access</Button></div> :
        <p>Ask a question to start this record’s shared conversation.</p>}
    </div>
    <form className="max-h-[55%] shrink-0 space-y-2 overflow-y-auto border-t bg-background p-3" onSubmit={event => { event.preventDefault(); void start(); }}>
      <p className="text-xs text-muted-foreground">Your question and answer are shared with current record readers.</p>
      <AgentComposerInput inputRef={composer} value={message} disabled={!writable} onChange={event => setMessage(event.target.value)}
        placeholder={presentation?.messagePlaceholder ?? 'Ask Agent about this record…'} sendDisabled={!writable || !message.trim() || libraryDirty} />
      <SharedAgentFooter files={<Button type="button" variant="outline" size="icon" className="size-8" disabled aria-label="Attach files to conversation" title="Send a question or create a shared chat before attaching files"><Paperclip className="size-4" /></Button>} context={record.context} disabled={!writable} blocked={!current || !record.canWrite || disabled || busy || libraryBusy || libraryDirty || uncertain}
        hasDraft={Boolean(message.trim())} onDictation={text => setMessage(previous => [previous, text].filter(Boolean).join(' ').slice(0, 4000))}
        onUtterance={async () => null} onInterrupt={() => undefined} onNewChat={() => onNewChat?.()} onActiveChange={setSpeechActive} />
      {error && <p role="alert">{error}</p>}
      {uncertain && <p role="status">The original chat creation needs checking. Your question has not been sent.</p>}
      {uncertain ? <Button type="button" disabled={busy || !record.canWrite} onClick={() => { void start(true); }}>Check original chat</Button>
        : null}
    </form>
    </div>
    <AgentPromptLibrary rail desktop={rails.prompts} scope={{ kind: 'entity', tenantId: record.context.tenantId, entityId: record.context.entityId }}
      identity={identity} canManageTeam={false} canManagePrompts={false} disabled={!writable} functionKey={record.prompts.functionKey} moduleKey={record.prompts.moduleKey}
      compact={compact} allowRun={false} draftText={message} onPrompt={prompt => prepareQuestion(prompt.content)}
      onSet={entry => { const content = entry.prompts?.map(prompt => prompt.content).join('\n\n'); if (content) prepareQuestion(content); }}
      onBusyChange={setLibraryBusy} onDirtyChange={setLibraryDirty} />
  </div>;
}
